import { PromptInput, PromptInputBody, PromptInputTextarea, type PromptInputMessage } from '@/ai-elements/prompt-input';
import { ChatInput, ChatInputState, ChatInputPreset } from '@brandclub/bc-widgets';
import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AttachmentsRow } from './AttachmentsRow';
import { AttachmentButton } from './AttachmentButton';
import { MicrophoneButton } from './MicrophoneButton';
import { SubmitButton } from './SubmitButton';
import { CallBennyButton } from './CallBennyButton';
import { buildPromptInputStyles } from './styles';
import { attachmentFailureMessage, prepareAttachments } from './prepareAttachments';

/** Below this viewport width the panel is on a phone, not a narrow desktop pane. */
const PHONE_MAX_WIDTH = 768;
/**
 * Tallest a phone's panel composer may be. Roughly one line plus padding — the
 * compact presets sit at 42–46. A cap, not a replacement, so a brand that chose
 * something even shorter keeps it.
 */
const PHONE_COMPOSER_MAX_HEIGHT = 46;

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export interface PromptInputConciergeProps {
  placeholder?: string;
  onSubmit: (text: string, files?: Array<{ type: 'file'; mediaType: string; url: string }>) => void;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onAttachClick?: () => void;
  onMicClick?: () => void;
  isStreaming?: boolean;
  /** Called when the user clicks the stop button during streaming */
  onStop?: () => void;
  /** Direction the input expands when content grows. Default is "up" */
  expandDirection?: 'up' | 'down';
  /** Maximum height for the textarea in pixels */
  maxHeight?: number;
  /** Widget ID for independent style configuration. Defaults to "concierge-chat-input" */
  widgetId?: string;
  /** Display name shown in the edit panel */
  displayName?: string;
  /** Override default ChatInput state values */
  initialState?: Partial<ChatInputState>;
  /** Override which preset definitions to use (e.g. PANEL_CHAT_INPUT_PRESETS for panel inputs) */
  presets?: Record<Exclude<ChatInputPreset, 'custom'>, Partial<ChatInputState>>;
  /**
   * When true, always apply the mobile-responsive styling regardless of viewport
   * width. Used for the embedded/panel concierge window which is visually narrow
   * even on desktop.
   */
  forceMobile?: boolean;
  /**
   * Plan 1 / Plan 7a (voice-chat-wiki) — render the "Call {assistantName}"
   * CTA alongside the existing icon row. Host wires the click handler to
   * `ConciergeContextProvider.openVoiceCall()`, which mounts the
   * full-screen `<VoiceCallOverlay>` above the unified
   * `/concierge?conversationId=:id` route (D-63). The mic dictation
   * button is hidden when the call CTA is rendered (the call gesture
   * supersedes the legacy dictation path).
   */
  showCallButton?: boolean;
  onCallClick?: () => void;
  /** Brand persona name for the CTA label. Defaults to "Benny". */
  voiceAssistantName?: string;
  /**
   * Optional full CTA label override (with `{assistantName}` already
   * interpolated by the host). When omitted, falls back to
   * `Call {assistantName}` inside CallBennyButton. D-61 removed the
   * brand-configurable `voiceBranding.copy.landingCtaLabel` source — the
   * prop remains for any host that still wants to override the label.
   */
  voiceCtaLabel?: string;
}

export const PromptInputConcierge: React.FC<PromptInputConciergeProps> = ({
  placeholder = 'Ask anything',
  onSubmit,
  disabled = false,
  isStreaming = false,
  onStop,
  value,
  onChange,
  expandDirection = 'up',
  maxHeight,
  widgetId = 'concierge-chat-input',
  displayName = 'Chat Input',
  initialState,
  presets,
  forceMobile = false,
  showCallButton = false,
  onCallClick,
  voiceAssistantName,
  voiceCtaLabel,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [hasAttachments, setHasAttachments] = useState(false);
  /**
   * True while attachments are being decoded and downscaled. Decoding a HEIC
   * from an iPhone takes seconds, and until this existed the composer looked
   * completely idle for that whole window — so the shopper tapped send again
   * (PetArmor QA row 52). It gates the submit button below.
   */
  const [isPreparingAttachments, setIsPreparingAttachments] = useState(false);

  const isMultiline = (value?.length ?? 0) > 40 || (value?.includes('\n') ?? false);

  const handleSubmit = useCallback(
    async (message: PromptInputMessage) => {
      if (!message.text?.trim() && (!message.files || message.files.length === 0)) {
        return;
      }

      // All-or-nothing: `prepareAttachments` either hands back every file or
      // reports a failure. It must never be possible to send the text of a
      // message whose evidence silently fell off on the way.
      setIsPreparingAttachments(true);
      let prepared;
      try {
        prepared = await prepareAttachments(message.files);
      } finally {
        setIsPreparingAttachments(false);
      }

      if (!prepared.ok) {
        toast.error(attachmentFailureMessage(prepared.reason));
        return;
      }

      const { fileParts } = prepared;
      onSubmit(message.text || '', fileParts.length > 0 ? fileParts : undefined);

      // On mobile (including forced-mobile panel), blur the textarea so iOS /
      // Android dismiss the on-screen keyboard after submit — matching ChatGPT.
      // Frees up vertical space for the streaming response. Desktop keeps focus
      // so users can continue typing follow-ups.
      const isMobileViewport =
        forceMobile || (typeof window !== 'undefined' && window.innerWidth < 768);
      if (isMobileViewport) {
        textareaRef.current?.blur();
      }
    },
    [onSubmit, forceMobile],
  );

  const handleError = useCallback((err: { code: string; message: string }) => {
    if (err.code === 'max_file_size') {
      toast.error('File size exceeds 10MB limit');
    } else if (err.code === 'accept') {
      toast.error('Please upload a valid image file');
    } else {
      toast.error(err.message);
    }
  }, []);

  return (
    <ChatInput widgetId={widgetId} displayName={displayName} initialState={initialState} presets={presets}>
      {(styles, themeIcons, resolveColor) => {
        const s = styles as ChatInputState & { fontFamilyCss?: string; [key: string]: any };
        const isMobile = forceMobile || (typeof window !== 'undefined' && window.innerWidth < 768);
        // The concierge PANEL on an actual phone. `forceMobile` alone is not it:
        // it means "this surface is narrow" and is true for the desktop bubble
        // too, which has room for the tall composer and should keep it.
        const isPhonePanel = forceMobile && typeof window !== 'undefined' && window.innerWidth < PHONE_MAX_WIDTH;

        // Which shape the composer takes: a tall two-row box (placeholder on one
        // line, icons on the next) or a single-line pill.
        //
        // Read the MOBILE override on mobile, like every other property below.
        // This used to key off the desktop `minHeight` unconditionally, so the
        // studio could not express "short on mobile" at all: setting
        // `mobile_minHeight` shrank the box but left the two-row layout.
        const effectiveMinHeight = isMobile && s.mobile_minHeight != null ? s.mobile_minHeight : s.minHeight;
        // …and on a phone the panel is ALWAYS the single-line pill, whatever the
        // brand's saved config says. With the keyboard up, the bubble is barely
        // taller than the composer, and a two-row box pushes the conversation off
        // screen. That is a property of the device, not a brand choice — so this
        // deliberately overrides `widgetConfig`, which otherwise wins over every
        // default (`{ ...defaultState, ...savedConfig }`). Desktop is untouched.
        const isExpandedPreset = isPhonePanel ? false : effectiveMinHeight >= 100;
        const isExpanded = isMultiline || hasAttachments || isExpandedPreset;
        const effectiveBorderRadiusSingleLine =
          isMobile && s.mobile_borderRadiusSingleLine != null
            ? s.mobile_borderRadiusSingleLine
            : styles.borderRadiusSingleLine;
        const effectiveBorderRadiusMultiLine =
          isMobile && s.mobile_borderRadiusMultiLine != null
            ? s.mobile_borderRadiusMultiLine
            : styles.borderRadiusMultiLine;
        const borderRadius = isExpanded
          ? `${effectiveBorderRadiusMultiLine}px`
          : `${effectiveBorderRadiusSingleLine}px`;
        const borderWidth = styles.borderWidth ?? 1;
        const borderColor = resolveColor(styles.borderColor);
        const borderAsShadow = `0 0 0 ${borderWidth}px ${borderColor}`;
        const combinedShadow =
          styles.shadowEnabled && styles.shadow ? `${styles.shadow}, ${borderAsShadow}` : borderAsShadow;

        // Create responsive styles object that uses mobile values on mobile screens
        const responsiveStyles = {
          ...styles,
          iconSize: isMobile && s.mobile_iconSize ? s.mobile_iconSize : styles.iconSize,
          submitButtonSize: isMobile && s.mobile_submitButtonSize ? s.mobile_submitButtonSize : styles.submitButtonSize,
          iconGap: isMobile && s.mobile_iconGap ? s.mobile_iconGap : s.iconGap ?? 8,
        };

        return (
          <div className="tw:mx-auto tw:w-full" style={{ maxWidth: `${styles.maxWidth}px` }}>
            <div
              className="concierge-prompt-input"
              style={{
                borderRadius,
                backgroundColor: resolveColor(styles.backgroundColor),
                boxShadow: combinedShadow,
                transition: 'border-radius 0.15s ease-out',
              }}
            >
              <style>
                {buildPromptInputStyles({
                  focusBorderColor: resolveColor(styles.borderColorFocus),
                  fontSize: styles.fontSize,
                  fontWeight: styles.fontWeight,
                  fontFamilyCss: s.fontFamilyCss || 'inherit',
                  textColor: resolveColor(styles.textColor),
                  placeholderColor: resolveColor(styles.placeholderColor),
                  submitBgColor: resolveColor(styles.submitButtonBgColor),
                  submitBgColorHover: resolveColor(styles.submitButtonBgColorHover),
                  mobileFontSize: s.mobile_fontSize ?? 14,
                  mobilePaddingY: s.mobile_paddingY ?? styles.paddingY,
                  mobilePaddingX: s.mobile_paddingX ?? styles.paddingX,
                  // Capped on a phone so forcing the single-line LAYOUT doesn't
                  // leave a one-line pill inside a 116px-tall box.
                  mobileMinHeight: isPhonePanel
                    ? Math.min(s.mobile_minHeight ?? s.minHeight ?? 0, PHONE_COMPOSER_MAX_HEIGHT)
                    : (s.mobile_minHeight ?? s.minHeight ?? 0),
                  mobileIconGap: s.mobile_iconGap ?? s.iconGap ?? 8,
                  mobileIconSize: s.mobile_iconSize ?? styles.iconSize,
                  mobileSubmitButtonSize: s.mobile_submitButtonSize ?? styles.submitButtonSize,
                  forceMobile,
                })}
              </style>
              <PromptInput
                onSubmit={handleSubmit}
                multiple
                globalDrop
                accept="image/*"
                maxFiles={5}
                maxFileSize={MAX_FILE_SIZE}
                onError={handleError}
                className="tw:w-full"
              >
                <PromptInputBody className="tw:flex tw:flex-col tw:w-full">
                  <AttachmentsRow onAttachmentsChange={setHasAttachments} styles={styles} />

                  <div
                    className="tw:flex tw:w-full concierge-input-container"
                    style={{
                      flexDirection: isExpanded ? 'column' : 'row',
                      alignItems: isExpanded ? 'stretch' : 'center',
                      ...(s.minHeight > 0
                        ? {
                            minHeight: `${
                              isPhonePanel ? Math.min(s.minHeight, PHONE_COMPOSER_MAX_HEIGHT) : s.minHeight
                            }px`,
                          }
                        : undefined),
                      // When expanded (multi-line / has attachments), add 4px to
                      // top and left so the text has a bit more breathing room
                      // from the pill border.
                      padding: isExpanded
                        ? `${styles.paddingY + 4}px ${styles.paddingX}px ${styles.paddingY}px ${styles.paddingX + 4}px`
                        : `${styles.paddingY}px ${styles.paddingX}px`,
                      boxSizing: 'border-box',
                      transition: 'all 0.2s ease-out',
                      cursor: 'text',
                    }}
                    onClick={(e) => {
                      // Tap-anywhere-in-the-pill-to-focus UX, but skip clicks
                      // that originated on the icon buttons. Otherwise tapping
                      // the attachment / mic / submit button re-focuses the
                      // textarea via bubbling, which on iOS brings the soft
                      // keyboard back up for a frame — visible as a "keyboard
                      // flash" right before the native file-picker sheet opens.
                      if ((e.target as HTMLElement).closest('.concierge-prompt-input-icons')) return;
                      textareaRef.current?.focus();
                    }}
                  >
                    <div
                      className={isExpanded ? 'tw:flex-1 tw:min-w-0' : 'tw:flex-1 tw:min-w-0 tw:flex tw:items-center'}
                      style={isExpandedPreset ? { overflow: 'auto' } : undefined}
                    >
                      <PromptInputTextarea
                        ref={textareaRef}
                        placeholder={placeholder}
                        // The composer has no visible label — only a
                        // placeholder, which is not a reliable accessible name
                        // (it disappears on input and some AT skip it entirely).
                        aria-label={placeholder}
                        disabled={disabled}
                        value={value}
                        onChange={(e) => onChange?.(e.target.value)}
                        rows={1}
                        data-expanded={isExpanded ? 'true' : undefined}
                        style={{
                          minHeight: 'auto',
                          maxHeight: maxHeight ?? Math.ceil(styles.fontSize * 1.5 * 5),
                          overflowY: 'auto',
                          paddingTop: 0,
                          paddingBottom: 0,
                          paddingLeft: 0,
                          paddingRight: 0,
                          transition: 'padding 0.2s ease-out',
                        }}
                      />
                    </div>

                    <div
                      className="tw:flex tw:items-center tw:shrink-0 concierge-prompt-input-icons"
                      data-expanded={isExpanded ? 'true' : undefined}
                      style={{
                        gap: responsiveStyles.iconGap,
                        paddingRight: 0,
                        paddingLeft: 0,
                        paddingBottom: 0,
                        paddingTop: isExpanded ? 8 : 0,
                        justifyContent: isExpanded ? 'flex-end' : undefined,
                        transition: 'all 0.2s ease-out',
                      }}
                    >
                      {styles.showAttachmentButton && (
                        <AttachmentButton
                          disabled={disabled || isStreaming}
                          styles={responsiveStyles}
                          resolveColor={resolveColor}
                        />
                      )}
                      {/*
                        Mic dictation button is hidden whenever the "Call
                        {assistantName}" CTA is rendered: dictation is
                        redundant once a true voice mode is one tap away.
                        Brands that disable voice still get the mic button
                        as before. The call button itself is icon-only at
                        `styles.iconSize`, so it occupies the exact same
                        slot the mic would have.
                      */}
                      {styles.showMicrophoneButton && !(showCallButton && onCallClick) && (
                        <MicrophoneButton
                          disabled={disabled || isStreaming}
                          textareaRef={textareaRef}
                          onChange={onChange}
                          styles={responsiveStyles}
                          themeIcons={themeIcons}
                          resolveColor={resolveColor}
                        />
                      )}
                      {showCallButton && onCallClick && (
                        <CallBennyButton
                          disabled={disabled || isStreaming}
                          onClick={onCallClick}
                          assistantName={voiceAssistantName}
                          ctaLabel={voiceCtaLabel}
                          styles={responsiveStyles}
                          resolveColor={resolveColor}
                        />
                      )}
                      <SubmitButton
                        disabled={
                          disabled ||
                          // A second tap while the first submit is still
                          // decoding its attachments queued a duplicate turn
                          // with the same images and (post-`form.reset()`) no
                          // text.
                          isPreparingAttachments ||
                          (!isStreaming && !value?.trim() && !hasAttachments)
                        }
                        isStreaming={isStreaming}
                        onStop={onStop}
                        styles={responsiveStyles}
                        resolveColor={resolveColor}
                      />
                    </div>
                  </div>
                </PromptInputBody>
              </PromptInput>
            </div>
          </div>
        );
      }}
    </ChatInput>
  );
};
